/* ═══════════════════════ what the work has cost ═══════════════════════

   The measurement layer: what studyHub has learned about how long each kind of
   work really takes, and what it is therefore willing to claim about work not
   done yet.

   This is the part of the app that everything else is built to serve. It sits
   in `state/` rather than `core/` because it reads `S` directly — core is the
   part that is pure, and this is a query over the state. It is not in any tab
   because four different callers need it and none of them owns it: the task panel quotes it while you type an
   estimate, the horizon prices a term with it, the week grid prices a day, and
   the planner engine takes all of it as host bindings.

   The ladder is the shape to preserve. `rateStats` measures minutes per page
   or per problem where the work says how many; `historyStats` measures minutes
   per kind of task where it does not; `standingMultiple` is the blunt ratio of
   estimated to actual across everything finished. Each rung needs two
   observations before it will speak, and when none of them will, the honest
   answer is the estimate you typed, labelled as exactly that.

   Nothing here invents a number. */

import { S } from './store.js';
import { courseName } from './store.js';
import {
  taskShape, median, p80, estBaseline, elapsedSec,
  seriesKind, quantityAttr, choiceAttrs, wkMode,
} from '../core/domain.js';
import StudyHubEngine from '../core/engine.js';

/** Average of actual÷estimated over finished tasks. null when there is no history yet. */
export function standingMultiple() {
  const ms = S.tasks.filter((t) => t.done && typeof t.multiple === 'number' && isFinite(t.multiple)).map((t) => t.multiple);
  if (!ms.length) return null;
  return ms.reduce((a, b) => a + b, 0) / ms.length;
}

export const deadlineById = (id) => S.deadlines.find((d) => d.id === id) || null;
export const tasksForDeadline = (id) => S.tasks.filter((t) => t.deadlineId === id);

/** Work owed to a deadline, in estimate minutes and in projected minutes. */
export function deadlineLoad(d) {
  const linked = tasksForDeadline(d.id);
  const open = linked.filter((t) => !t.done);
  const m = standingMultiple() || 1;
  const estLeft = open.reduce((a, t) => a + t.estMin, 0);
  // credit time already logged against the open tasks — you only owe the rest
  const loggedMin = open.reduce((a, t) => a + elapsedSec(t) / 60, 0);
  const projLeft = Math.max(0, estLeft * m - loggedMin);
  return {
    linked, open,
    doneCount: linked.length - open.length,
    estLeft,
    projLeft,
    msLeft: d.due - Date.now(),
    fits: projLeft * 60000 <= d.due - Date.now(),
  };
}

/** Finished tasks, grouped into "this kind of work, in this course, takes X". */
export function historyStats() {
  const done = S.tasks.filter((t) => t.done && typeof t.multiple === 'number' && isFinite(t.multiple) && t.actualMin > 0);

  const bucket = new Map();
  done.forEach((t) => {
    const shape = taskShape(t.title);
    // keyed by course as well, because a Stat pset and a Gov pset are not the
    // same animal even though they share a name
    const key = `${t.courseId || ''}|${shape}`;
    if (!bucket.has(key)) bucket.set(key, { shape, courseId: t.courseId || '', actuals: [], ests: [], mults: [] });
    const b = bucket.get(key);
    b.actuals.push(t.actualMin);
    b.ests.push(estBaseline(t));
    b.mults.push(t.multiple);
  });

  return [...bucket.values()]
    .map((b) => ({
      shape: b.shape,
      group: courseName(b.courseId),
      n: b.actuals.length,
      medianActualMin: Math.round(median(b.actuals)),
      p80ActualMin: Math.round(p80(b.actuals)),
      medianEstMin: Math.round(median(b.ests)),
      medianMultiple: +median(b.mults).toFixed(2),
    }))
    .sort((a, b) => b.n - a.n)
    .slice(0, 24);
}

/** What this week's item will really cost, and why we believe that.

    This used to be its own two-rung ladder, and that was the trouble: the
    horizon, the week grid and the intake modal each had one, and three ladders
    of unequal strength quote three different numbers for one problem set. The
    strongest of them is the planner's, so it is now the only one — the same
    call, from every screen, so the number a row shows is the number the plan
    was built on.

    The return gains `rung` and `high`; the callers that only ever read `min`,
    `basis` and `n` carry on unchanged. */
export function predictMin(item) {
  /* A lecture and a gym slot both cost exactly as long as they are booked for,
     so they go in as `fixed` and come back out again without touching the
     ladder. `kind` is the WK_KINDS key: the engine maps it to the series kind
     that shares its measurements, so a weekly reading item is priced from the
     same pages-per-minute bucket a reading series instance is. */
  return StudyHubEngine.price({
    kind: item.kind,
    mode: wkMode(item.kind) === 'work' ? 'work' : 'fixed',
    title: item.title,
    courseId: item.courseId,
    estMin: item.estMin || 0,
    durMin: item.durMin || 0,
    attrs: null,
  });
}

/* ─────────────────── the week being looked at ─────────────────── */

/* The planner always shows the current week. A planner you can page backwards
   through is a calendar, and studyHub already has one of those. */

export function rateStats() {
  const done = S.tasks.filter((t) => {
    if (!(t.done && t.actualMin > 0 && t.attrs && t.series)) return false;
    // Only a kind that counts something — pages, problems — can have a rate.
    const q = quantityAttr(t.series.kind);
    return !!q && +t.attrs[q.key] > 0;
  });

  /** @type {Map<string, any>} */
  const bucket = new Map();
  done.forEach((t) => {
    const kind = t.series.kind;
    // The filter above already established that this kind has a quantity.
    const q = /** @type {any} */ (quantityAttr(kind));
    const facet = choiceAttrs(kind).map((a) => t.attrs[a.key]).filter(Boolean).join('/');
    const key = `${t.courseId || ''}|${kind}|${facet}`;

    if (!bucket.has(key)) bucket.set(key, { kind, facet, courseId: t.courseId || '', unit: q.unit, rates: [], qtys: [], actuals: [] });
    const b = bucket.get(key);
    b.rates.push(t.actualMin / +t.attrs[q.key]);
    b.qtys.push(+t.attrs[q.key]);
    b.actuals.push(t.actualMin);
  });

  return [...bucket.values()]
    .map((b) => ({
      kind: b.kind,
      kindLabel: seriesKind(b.kind).label,
      facet: b.facet,
      group: courseName(b.courseId),
      courseId: b.courseId,
      unit: b.unit,
      n: b.rates.length,
      medianMinPerUnit: +median(b.rates).toFixed(2),
      p80MinPerUnit: +p80(b.rates).toFixed(2),
      medianQty: Math.round(median(b.qtys)),
      medianActualMin: Math.round(median(b.actuals)),
    }))
    .sort((a, b) => b.n - a.n)
    .slice(0, 24);
}

/* What this instance will cost, and — just as important — how much that answer
   is worth. Each rung is a weaker claim than the one above it, so the basis is
   returned alongside the number and shown wherever the number is: a measurement
   and a guess must never look alike. */
export function predictForAttrs(courseId, kind, attrs, fallbackEstMin) {
  const q = quantityAttr(kind);
  const qty = q && attrs ? +attrs[q.key] || 0 : 0;

  if (q && qty > 0) {
    const facet = choiceAttrs(kind).map((a) => attrs[a.key]).filter(Boolean).join('/');
    const stats = rateStats();

    const exact = stats.find((s) => s.kind === kind && s.facet === facet && s.courseId === courseId);
    const anyCourse = stats.find((s) => s.kind === kind && s.facet === facet);
    const hit = [exact, anyCourse].find((s) => s && s.n >= 2);

    if (hit) {
      const label = facet ? `${facet} ` : '';
      return {
        min: Math.max(1, Math.round(qty * hit.medianMinPerUnit)),
        basis: `${qty} ${qty === 1 ? q.unit : q.plural} × ${hit.medianMinPerUnit} min/${q.unit}`
             + ` — from ${hit.n} finished ${label}${seriesKind(kind).label.toLowerCase()}s`
             + (exact && exact.n >= 2 ? ` in ${hit.group}` : ''),
        rung: 'rate',
        n: hit.n,
      };
    }
  }

  /* No rate yet. Fall through to what the app already knew before this feature
     existed: the median duration of this shape of work, then the multiple. */
  const shape = seriesKind(kind).shape;
  if (shape) {
    const stats = historyStats();
    const exact = stats.find((s) => s.shape === shape && s.group === courseName(courseId));
    const anyCourse = stats.find((s) => s.shape === shape);
    const hit = [exact, anyCourse].find((s) => s && s.n >= 2);
    if (hit) {
      return {
        min: hit.medianActualMin,
        basis: `${hit.n} finished ${hit.shape}${hit.n === 1 ? '' : 's'}${exact && exact.n >= 2 ? ` in ${hit.group}` : ''}`
             + ' — not yet measured per unit',
        rung: 'median',
        n: hit.n,
      };
    }
  }

  const m = standingMultiple();
  const est = fallbackEstMin || 0;
  if (m) return { min: Math.round(est * m), basis: `your estimate through your ${m.toFixed(2)}× multiple`, rung: 'multiple', n: 0 };
  return { min: est, basis: 'your estimate — nothing measured yet', rung: 'estimate', n: 0 };
}
